import { useCallback } from 'react';
import { calculatedFieldPath } from 'resources/rate_settings/utils/fieldsVisibility';
import type {
  FieldValues,
  UseFormSetError,
  UseFormClearErrors,
  UseFormWatch,
  UseFormSetValue,
} from 'react-hook-form';

type UseScalingMarginEffectProps = {
  setError: UseFormSetError<FieldValues>;
  clearErrors: UseFormClearErrors<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  source: string;
  applicableField: string;
  isW2Local: boolean;
};

export const useScalingMarginEffect = ({
  setError,
  clearErrors,
  watch,
  setValue,
  source,
  applicableField,
  isW2Local,
}: UseScalingMarginEffectProps) => {
  const is1099 = source.includes('ten99');

  const key = is1099 ? '1099' : 'w2';

  const marginPercentGuardrail = watch(`margin_percent_${key}_guardrail`);
  const incrementalMarginPercent = watch(`incremental_margin_percent_${key}`);

  let marginFieldName: string;
  if (is1099) {
    marginFieldName = calculatedFieldPath(source, 'margin_percent_1099', watch);
  } else if (source.includes('w2.local')) {
    marginFieldName = `${source}.base_margin_percent_w2`;
  } else if (source.includes('w2.travel')) {
    marginFieldName = calculatedFieldPath(source, 'margin_percent_w2_travel', watch);
  }

  const marginValue = watch(marginFieldName);

  const validateGuardrailRange = useCallback(
    (value: number, fieldName: string, fieldLabel: string) => {
      if (value == null || value === undefined) {
        clearErrors(fieldName);
        return true;
      }

      if (value < 0 || value > 1) {
        setError(
          fieldName,
          {
            type: 'manual',
            message: `${fieldLabel} should be between 0 and 1`,
          },
          {
            shouldFocus: true,
          },
        );
        return false;
      } else {
        clearErrors(fieldName);
        return true;
      }
    },
    [setError, clearErrors],
  );

  const validateGuardrailVsMargin = useCallback(
    (
      guardrailValue: number,
      marginVal: number,
      guardrailFieldName: string,
      marginLabel: string,
    ) => {
      if (guardrailValue == null || marginVal == null) {
        return true;
      }

      if (guardrailValue > marginVal) {
        setError(
          guardrailFieldName,
          {
            type: 'manual',
            message: `Margin Percent ${key?.toUpperCase()} Guardrail should not be above ${marginLabel}`,
          },
          {
            shouldFocus: true,
          },
        );
        return false;
      }

      clearErrors(guardrailFieldName);

      const fieldName = `${source}.${applicableField}`;
      clearErrors(fieldName);
      return true;
    },
    [setError, clearErrors, key, source, applicableField],
  );

  const validateIncrementalMargin = useCallback(
    (value: number, currentGuardrailValue?: number) => {
      const fieldName = `incremental_margin_percent_${key}`;
      const fieldLabel = `Incremental Margin Percent ${key?.toUpperCase()}`;
      const effectiveGuardrail = currentGuardrailValue ?? marginPercentGuardrail;

      if (value == null || value === undefined) {
        clearErrors(fieldName);
        return true;
      }

      // Check if incremental margin >= guardrail
      if (effectiveGuardrail != null && value < effectiveGuardrail) {
        setError(
          fieldName,
          {
            type: 'manual',
            message: `${fieldLabel} should not be below Margin Percent ${key?.toUpperCase()} Guardrail`,
          },
          {
            shouldFocus: true,
          },
        );
        setValue(fieldName, value, { shouldTouch: true, shouldValidate: false });
        return false;
      }

      // Check if incremental margin < 0.49
      if (value >= 0.49) {
        setError(
          fieldName,
          {
            type: 'manual',
            message: `${fieldLabel} should be below 0.49`,
          },
          {
            shouldFocus: true,
          },
        );
        return false;
      }

      clearErrors(fieldName);
      return true;
    },
    [key, marginPercentGuardrail, clearErrors, setError, setValue],
  );

  const validateMarginPercentGuardrail = useCallback(
    (value: number, currentMarginValue?: number) => {
      if (!key) {
        return;
      }

      const fieldName = `margin_percent_${key}_guardrail`;
      const fieldLabel = `Margin Percent ${key.toUpperCase()} Guardrail`;

      const rangeValid = validateGuardrailRange(value, fieldName, fieldLabel);

      if (!rangeValid) {
        return;
      }

      const marginLabel = is1099
        ? 'Margin Percent 1099'
        : source.includes('w2.local')
        ? 'Base Margin Percent W2'
        : 'Margin Percent W2 Travel';

      const marginToCompare = currentMarginValue ?? marginValue;
      validateGuardrailVsMargin(value, marginToCompare, fieldName, marginLabel);
      validateIncrementalMargin(incrementalMarginPercent, value);
    },
    [
      key,
      validateGuardrailRange,
      is1099,
      source,
      marginValue,
      validateGuardrailVsMargin,
      validateIncrementalMargin,
      incrementalMarginPercent,
    ],
  );

  const validateMarginAgainstGuardrail = useCallback(
    (value: number) => {
      if (marginPercentGuardrail == null || value == null) {
        clearErrors(applicableField);
        return;
      }

      const fieldName = `${source}.${applicableField}`;
      const is1099 = source.includes('ten99');
      const marginLabel = is1099
        ? 'Margin Percent 1099'
        : isW2Local
        ? 'Base Margin Percent W2'
        : 'Margin Percent W2 Travel';

      const key = is1099 ? '1099' : 'W2';

      if (value < marginPercentGuardrail) {
        setError(
          fieldName,
          {
            type: 'manual',
            message: `${marginLabel} should not be below Margin Percent ${key} Guardrail`,
          },
          {
            shouldFocus: true,
          },
        );
      } else {
        clearErrors(fieldName);
        validateMarginPercentGuardrail(marginPercentGuardrail, value);
      }
    },
    [
      marginPercentGuardrail,
      source,
      applicableField,
      isW2Local,
      setError,
      clearErrors,
      validateMarginPercentGuardrail,
    ],
  );

  return {
    validateMarginPercentGuardrail,
    validateIncrementalMargin,
    marginPercentGuardrail,
    incrementalMarginPercent,
    validateMarginAgainstGuardrail,
  };
};
